import type Resources from "@/@types/resources";
import type { ToasterToast } from "@/components/providers/ToasterProvider";
import { z } from "zod";
import { toaster } from "@/components/providers/ToasterProvider";
import errorsEn from "@/locales/en/errors.json";
import { isApiError, markApiErrorHandled } from "@/services/api";
import { tokenErrorCodes } from "@/services/api/handle-auth";
import { $t } from "@/services/i18n";

type ErrorDisplayMode = "basic" | "detailed";

// Status de VALIDAÇÃO de input do usuário: 400/422 são, por definição, fluxo
// esperado (o toast é o tratamento). Só esses podem ser silenciados no Sentry
// quando escapam como unhandled rejection. 5xx (bug de servidor), 404, 409 e
// afins NÃO são marcados — mesmo com toast, continuam visíveis no Sentry como
// possível bug real. Ver `notifyError` e `unhandled-rejection.ts` (AI-977).
const SUPPRESSIBLE_STATUS = new Set([400, 422]);

const ErrorSchema = z.object({
  error: z.object({
    // The OpenRouter-compatibility facades put the HTTP status in `code` and the
    // catalog code in `platform_code`; native routes keep the string code.
    code: z.union([z.string(), z.number()]).nullish(),
    platform_code: z.string().nullish(),
    message: z.string().optional(),
    statusCode: z.number().optional(),
    details: z.array(z.object({ message: z.string() })).optional(),
    name: z.string().optional(),
  }),
});

export function handleApiError(err: unknown) {
  console.error(err);
  // check if error is an API error
  if (!isApiError(err)) {
    return;
  }

  if (!err.response) {
    toaster.error("Network Error");
    return;
  }

  if (err.response.status === 401) {
    console.error(err);
    return;
  }

  if (isLoopbackError(err.response.data)) {
    const msg = extractErrorMessage(err.response.data);

    toaster.error(msg ?? "Ocorreu um erro.");
    return;
  }
}

const LoopbackErrorSchema = z.object({
  error: z.object({
    code: z.string().nullish(),
    message: z.string(),
    statusCode: z.number(),
    details: z.unknown().optional(),
    name: z.string(),
  }),
});

type LoopbackError = z.infer<typeof LoopbackErrorSchema>;

export function isLoopbackError(err: unknown): err is LoopbackError {
  return (
    !!err &&
    typeof err === "object" &&
    "error" in err &&
    typeof err.error === "object" &&
    "statusCode" in err.error!
  );
}

export function extractLopbackError(err: unknown): LoopbackError | undefined {
  if (!isApiError(err)) return undefined;

  if (!err.response) return undefined;
  if (!err.response.data) return undefined;

  try {
    return LoopbackErrorSchema.parse(err.response.data);
  } catch (_error) {
    return undefined;
  }
}

export function isServerError(err: unknown, code: string): boolean {
  return extractLopbackError(err)?.error.code === code;
}

function extractErrorMessage(error: any): string {
  if (Array.isArray(error.details)) {
    return error.details.map((d: { message: string }) => d?.message ?? "").join("\n");
  }

  if (error?.message) return error.message;

  return "Ocorreu um erro.";
}

export function extractErrorCode(err: unknown): string | undefined {
  return extractLopbackError(err)?.error.code ?? undefined;
}

type ErrorCode = keyof Resources["errors"];

const KNOWN_ERROR_CODES: ReadonlySet<string> = new Set(Object.keys(errorsEn));

/**
 * Traduz um codigo de erro que so existe em runtime (veio do servidor).
 * A conversao para chave tipada mora aqui, nao nos call sites. Codigo
 * desconhecido cai no fallback generico em vez de vazar a chave crua
 * ("errors:foo") para o usuario.
 */
export function translateErrorCode(code: string): string {
  if (KNOWN_ERROR_CODES.has(code)) {
    return $t(`errors:${code as ErrorCode}`);
  }
  return $t("errors:error_occurred");
}

export const notifyError = async (err: unknown, mode: ErrorDisplayMode = "basic") => {
  console.error(err);
  const error = extractLopbackError(err);

  if (!tokenErrorCodes.includes(error?.error.code ?? "")) {
    toaster.error(parseErrorMessage(err, mode));
    // Erro de validação (400/422) já exibido ao usuário: marca como tratado
    // para que, se a rejeição re-lançada abaixo escapar como
    // `onunhandledrejection` (ex.: em event handlers Vue não aguardados), o net
    // global / Sentry beforeSend suprimam o ruído. NÃO marcamos 5xx/404/409 nem
    // erros sem status (rede): mesmo toasted, seguem visíveis no Sentry como
    // possível bug real.
    if (isApiError(err) && SUPPRESSIBLE_STATUS.has(err.response?.status ?? 0)) {
      markApiErrorHandled(err);
    }
  }

  throw err;
};

type ErrorCopy = { title: string; message: string };

/**
 * Resolve the displayed title/message for an error body. Prefers the catalog
 * `platform_code` from the numeric facade envelope, falls back to the legacy
 * string `code`; a numeric-only code has no translation key, so the safe server
 * message is used.
 */
function resolveErrorCopy(error: {
  code?: string | number | null;
  platform_code?: string | null;
  message?: string;
}): ErrorCopy {
  const platformCode = typeof error.platform_code === "string" && error.platform_code.length > 0
    ? error.platform_code
    : undefined;
  const stringCode = typeof error.code === "string" && error.code.length > 0
    ? error.code
    : undefined;
  const translationCode = platformCode ?? stringCode;

  return {
    title: translationCode
      ? translateErrorCode(translationCode)
      : (error.message ?? $t("errors:error_occurred")),
    message: error.message ?? $t("errors:no_details_available"),
  };
}

export function parseErrorMessage(
  err: unknown,
  mode: ErrorDisplayMode = "basic"
): string | Pick<ToasterToast, "title" | "description"> {
  // Network error: fetch failed, no response (server down, no connection, etc.)
  if (isApiError(err) && !err.response) {
    return $t("errors:network_error");
  }

  if (isApiError(err)) {
    const errorData = ErrorSchema.safeParse(err.response?.data);

    if (errorData.success) {
      const { title, message } = resolveErrorCopy(errorData.data.error);

      if (mode === "detailed") {
        return {
          title,
          description: message,
        };
      }

      return title;
    }
  }

  if (err instanceof Error) {
    return {
      title: $t("errors:error_occurred"),
      description: $t("errors:client_error"),
    };
  }

  const fallbackMessage = $t("errors:error_occurred");
  if (mode === "detailed") {
    return {
      title: $t("errors:generic_error"),
      description: fallbackMessage,
    };
  }

  return fallbackMessage;
}

type ErrorMap = Record<string, (err: unknown) => void | never>;

export const matchError = (map: ErrorMap) => async (err: unknown) => {
  const code = extractErrorCode(err);

  // Retorna o handler em AMBOS os ramos. Sem o return/await, o ramo `otherwise`
  // (cujo default é notifyError, async e que re-lança) gerava uma promise
  // rejeitada órfã => `onunhandledrejection` garantido. Agora a rejeição flui
  // para o `.catch` do chamador.
  const handler = code && code in map ? map[code] : (map["otherwise"] ?? ((e: unknown) => notifyError(e)));

  return handler(err);
};
